import { pick } from 'lodash';
import { isProtocolLink, } from "../entities";
import { FIXED_PROTOCOL_CONTENT_TYPE_IDS } from './ProtocolTypeUtils';
export function getEquipmentItemSchema(equipmentItem, equipmentList, protocolContentType) {
    const adjustments = equipmentList.protocolContentTypes.find((config) => config.ref._id === protocolContentType._id)?.adjustments || [];
    return protocolContentType.schema.reduce((schema, element) => {
        if (element.type === undefined) {
            return [...schema, element];
        }
        const config = equipmentItem.contentTypeConfig.find((config) => config.contentType._id === protocolContentType._id && config.elementId === element._id);
        if (config && config.disabled) {
            return [...schema];
        }
        const adjustment = adjustments.find((adjustment) => adjustment.elementId === element._id);
        if (!adjustment) {
            return [...schema, element];
        }
        const itemElementForCount = equipmentList.itemSchema.find((itemElement) => itemElement.type !== undefined && itemElement._id === adjustment.itemElementIdForCount);
        const count = itemElementForCount ? equipmentItem.itemData[itemElementForCount.name] : 1;
        if (count === 1) {
            return [...schema, element];
        }
        const repeatedElement = Array.from({ length: count }, (_, i) => i + 1).map((i) => ({
            ...element,
            name: `${element.name}_${i}`,
            label: `${element.label} (${i})`,
        }));
        return [...schema, ...repeatedElement];
    }, []);
}
export function getChecklistSchema(checklistType) {
    return checklistType.checkpoints.map((checkpoint) => {
        if (checkpoint.type === 'checkbox') {
            return { _id: checkpoint._id, type: checkpoint.type, name: checkpoint._id, label: checkpoint.text };
        }
        return {
            _id: checkpoint._id,
            type: checkpoint.type,
            name: checkpoint._id,
            label: checkpoint.text,
            items: checkpoint.options.map((option) => ({ value: option.option, label: option.option })),
        };
    });
}
export function getRemarksSchema(label) {
    return [
        {
            _id: FIXED_PROTOCOL_CONTENT_TYPE_IDS.REMARKS,
            name: 'remarks',
            label: label,
            type: 'textarea',
            validation: [],
        },
    ];
}
export function isElementForConditional(element) {
    if (element.type === undefined) {
        return false;
    }
    return element.type === 'autocomplete' || element.type === 'combobox' || element.type === 'checkbox';
}
export function repeatSchema(schema, number) {
    if (number <= 0) {
        throw Error(`Invalid repetition number ${number}`);
    }
    return schema.map((element) => {
        if (element.type === undefined) {
            return element;
        }
        return { ...element, name: `${element.name}_${number}` };
    });
}
export function getRepeatedSchema(schema, count) {
    if (count === 1) {
        return [schema];
    }
    return Array.from({ length: count }, (_, i) => i + 1).map((i) => repeatSchema(schema, i));
}
export function getElementValue(protocol, contentType, elementId) {
    const content = protocol.contents.find((content) => content.type._id === contentType._id);
    const element = getElement(contentType, elementId);
    if (!element || !content) {
        return undefined;
    }
    const elementValue = content.data[element.name];
    return elementValue;
}
export function getGroupItems(protocol, contentType, groupElement) {
    const groupItems = getElementValue(protocol, contentType, groupElement._id);
    if (!Array.isArray(groupItems)) {
        return [];
    }
    return groupItems;
}
export function getSubElementValue(data, groupElement, subElementId) {
    const subElement = getElement(getGroupChildrenElements(groupElement), subElementId);
    if (!subElement) {
        return undefined;
    }
    return data[subElement.name];
}
export function getElements(contentType) {
    const schema = Array.isArray(contentType) ? contentType : contentType.schema;
    return schema.filter((element) => element.type !== undefined);
}
export function getGroupChildrenElements(group) {
    if (group === undefined || group.type !== 'group' || !group.children || !Array.isArray(group.children)) {
        return [];
    }
    return getElements(group.children);
}
export function getElement(contentType, elementId) {
    return getElements(contentType).find((element) => element._id === elementId);
}
export function getSchemaElementNames(schema) {
    return getElements(schema || []).map((element) => element.name);
}
export function cleanupSchemaData(schema, data) {
    const elements = getElements(schema);
    const cleanedData = pick(data, elements.map((element) => element.name));
    elements
        .filter((element) => element.type === 'group')
        .forEach((element) => {
        if (Array.isArray(element.children) && cleanedData[element.name]) {
            const items = cleanedData[element.name];
            cleanedData[element.name] = items.map((item) => cleanupSchemaData(element.children, item));
        }
    });
    elements
        .filter((element) => element.type === 'object')
        .forEach((element) => {
        if (Array.isArray(element.children) && cleanedData[element.name]) {
            cleanedData[element.name] = cleanupSchemaData(element.children, cleanedData[element.name]);
        }
    });
    return cleanedData;
}
export function matchesElementValue(app, protocol, condition) {
    if (!condition) {
        return true;
    }
    const conditionValue = getElementValue(protocol, app.get('localCache').getProtocolContentType(condition.protocolContentType._id), condition.elementId);
    if (isProtocolLink(conditionValue)) {
        return conditionValue.value === condition.value;
    }
    return conditionValue === condition.value;
}
export function withPeerDependencies(contentType, getProtocolContentType, existingDependencies = []) {
    if (typeof getProtocolContentType !== 'function') {
        return withPeerDependencies(contentType, (id) => getProtocolContentType.get('localCache').getProtocolContentType(id), existingDependencies);
    }
    const contentTypesToHandle = [contentType];
    const peerDependencies = [];
    const allExistingDependencies = existingDependencies.map((dependency) => dependency._id);
    while (contentTypesToHandle.length > 0) {
        const currentContentType = contentTypesToHandle.shift();
        if (currentContentType) {
            if (!allExistingDependencies.includes(currentContentType._id)) {
                peerDependencies.push(currentContentType);
                allExistingDependencies.push(currentContentType._id);
            }
            contentTypesToHandle.push(...(currentContentType.peerDependencies || [])
                .filter((ref) => !allExistingDependencies.includes(ref._id))
                .map((ref) => getProtocolContentType(ref._id))
                .filter((contentType) => contentType !== undefined));
        }
    }
    return peerDependencies;
}
export function areElementNamesUnique(contentTypes) {
    const overallSchema = contentTypes.flatMap(({ schema }) => schema);
    const elementNames = getSchemaElementNames(overallSchema);
    return elementNames.length === new Set(elementNames).size;
}
const defaultFilterDataBySchemaOptions = {
    peerDependencySchema: [],
    peerDependencyValue: {},
    parentSchema: [],
    parentValue: {},
};
export function filterDataBySchema(schema, data, options) {
    const { peerDependencySchema, peerDependencyValue, parentSchema, parentValue } = {
        ...defaultFilterDataBySchemaOptions,
        ...options,
    };
    const filteredData = { ...data };
    schema.forEach((element) => {
        if (element.type === undefined ||
            element.conditional === undefined ||
            filteredData[element.name] === undefined ||
            filteredData[element.name] === null) {
            return;
        }
        const conditions = !Array.isArray(element.conditional) ? [element.conditional] : element.conditional;
        const conditionsMatched = conditions.every(({ elementId: conditionElementId, value: conditionalValue, negateCondition }) => {
            const conditionElement = [...schema, ...peerDependencySchema].find((otherElement) => otherElement.type !== undefined && otherElement._id === conditionElementId);
            if (conditionElement) {
                if (negateCondition) {
                    return { ...data, ...peerDependencyValue }[conditionElement.name] !== conditionalValue;
                }
                return { ...data, ...peerDependencyValue }[conditionElement.name] === conditionalValue;
            }
            const conditionElementFromParent = parentSchema.find((otherElement) => otherElement.type !== undefined && otherElement._id === conditionElementId);
            if (conditionElementFromParent) {
                if (negateCondition) {
                    return parentValue[conditionElementFromParent.name] !== conditionalValue;
                }
                return parentValue[conditionElementFromParent.name] === conditionalValue;
            }
            return false;
        });
        if (!conditionsMatched) {
            delete filteredData[element.name];
        }
    });
    schema.forEach((element) => {
        if (element.type === undefined || filteredData[element.name] === undefined || filteredData[element.name] === null) {
            return;
        }
        if (element.type === 'group' && Array.isArray(element.children) && filteredData[element.name]) {
            const elementData = filteredData[element.name];
            const childrenSchema = element.children;
            if (Array.isArray(elementData)) {
                filteredData[element.name] = elementData.map((itemData) => filterDataBySchema(childrenSchema, itemData, {
                    peerDependencySchema,
                    peerDependencyValue,
                    parentSchema: schema,
                    parentValue: data,
                }));
            }
        }
    });
    return filteredData;
}
//# sourceMappingURL=SchemaUtils.js.map